/**
 * /clinic: the patient list, the form to add one, and the patient detail when the path names an
 * id (PRA-010 to PRA-014).
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { AddClinicPatientReply, ClinicPatientList, ClinicPatientRow } from "@lattice/core";
import { clinicRolesGrant } from "@lattice/core";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import type { BadgeTone } from "@lattice/ui/Badge";
import { Badge } from "@lattice/ui/Badge";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { ErrorState } from "@lattice/ui/ErrorState";
import { focusFirstError } from "@lattice/ui/focusFirstError";
import { Field } from "@lattice/ui/Field";
import { DateInput } from "@lattice/ui/DateInput";
import { Input } from "@lattice/ui/Input";
import { Pending } from "@lattice/ui/Pending";
import { Section } from "@lattice/ui/Section";
import { Select } from "@lattice/ui/Select";
import type { Column, Sort } from "@lattice/ui/Table";
import { Table } from "@lattice/ui/Table";
import { Text } from "@lattice/ui/Text";
import { EmailCodeSignIn } from "../EmailCodeSignIn.tsx";
import { addClinicPatient, listClinicPatients } from "../api.ts";
import { visuallyHidden } from "@lattice/ui/visuallyHidden";
import { navigate, usePath } from "../router.ts";
import { ClinicFormulaChangesPage } from "./Changes.tsx";
import { ClinicFormulaPage } from "./Formula.tsx";
import { ClinicPatientPage } from "./Patient.tsx";
import { LeaveGuard } from "./LeaveGuard.tsx";
import { ClinicShell } from "./Shell.tsx";
/* The browser's own email check is off with `noValidate`, so the form says it in its own words. */
function emailProblem(email: string): string | null {
  if (email.trim() === "") return "Enter an email address.";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? null : "Enter an email address like name@example.com.";
}
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "sign-in";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "home";
  readonly clinic: ClinicPatientList;
};

/* A practitioner's new patients are theirs. An assistant names which practitioner the patient is for. */
function picksPractitioner(clinic: ClinicPatientList): boolean {
  return clinic.roles.includes("assistant") && !clinic.roles.includes("practitioner");
}
function patientName(first: string | null, last: string | null): string {
  const name = [first, last].filter(part => part !== null).join(" ");
  return name === "" ? "Name not given" : name;
}
const REVIEW_STATUS: Readonly<Record<ClinicPatientRow["reviewStatus"], string | null>> = {
  none: null,
  waiting: "Review waiting",
  in_progress: "Review in progress",
  approved: "Review approved",
  edited: "Review edited",
  declined: "Review declined"
};
const ORDER_STATUS: Readonly<Record<ClinicPatientRow["orderStatus"], string | null>> = {
  none: null,
  pending_payment: "Order awaiting payment",
  paid: "Order paid",
  released: "Order released",
  cancelled: "Order cancelled",
  refunded: "Order refunded"
};
const FORMULA_BADGE: Readonly<Record<ClinicPatientRow["formulaStatus"], {
  readonly label: string;
  readonly tone: BadgeTone;
}>> = {
  none: {
    label: "No formula",
    tone: "neutral"
  },
  draft: {
    label: "Draft",
    tone: "neutral"
  },
  in_review: {
    label: "In review",
    tone: "info"
  },
  approved: {
    label: "Approved",
    tone: "ok"
  },
  declined: {
    label: "Declined",
    tone: "danger"
  },
  superseded: {
    label: "Superseded",
    tone: "neutral"
  }
};
const WAITING = {
  label: "Waiting to accept",
  tone: "warn"
} as const;
function badgeFor(patient: ClinicPatientRow) {
  return patient.status === "pending" ? WAITING : FORMULA_BADGE[patient.formulaStatus];
}

/** Where review and order stand, in words, or nothing when neither has started. */
function progressLine(patient: ClinicPatientRow): string {
  if (patient.status === "pending") return "";
  return [REVIEW_STATUS[patient.reviewStatus], ORDER_STATUS[patient.orderStatus]].filter(part => part !== null).join(" · ");
}
const UPDATED = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC"
});
function updatedOn(patient: ClinicPatientRow): string {
  if (patient.updatedAt === null) return "";
  const date = new Date(patient.updatedAt.includes("T") ? patient.updatedAt : `${patient.updatedAt}Z`);
  return Number.isNaN(date.getTime()) ? patient.updatedAt : UPDATED.format(date);
}
function nameOf(patient: ClinicPatientRow): string {
  return patientName(patient.firstName, patient.lastName);
}

/** What a column sorts on. An empty date sorts first ascending, which is oldest first. */
function sortKey(column: string, patient: ClinicPatientRow): string {
  switch (column) {
    case "practitioner":
      return (patient.practitionerName ?? "").toLowerCase();
    case "formula":
      return badgeFor(patient).label;
    case "updated":
      return patient.updatedAt ?? "";
    default:
      return nameOf(patient).toLowerCase();
  }
}
function sorted(patients: readonly ClinicPatientRow[], sort: Sort): readonly ClinicPatientRow[] {
  const flip = sort.direction === "asc" ? 1 : -1;
  return [...patients].sort((a, b) => sortKey(sort.column, a).localeCompare(sortKey(sort.column, b)) * flip);
}
function matching(patients: readonly ClinicPatientRow[], query: string): readonly ClinicPatientRow[] {
  const wanted = query.trim().toLowerCase();
  if (wanted === "") return patients;
  return patients.filter(patient => [nameOf(patient), patient.practitionerName ?? "", patient.email ?? ""].some(value => value.toLowerCase().includes(wanted)));
}
function AddForm({
  clinic,
  onAdded
}: {
  readonly clinic: ClinicPatientList;
  readonly onAdded: (clinic: ClinicPatientList, added: AddClinicPatientReply) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [email, setEmail] = useState("");
  const [practitionerUserId, setPractitionerUserId] = useState(clinic.practitioners[0]?.userId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  /* Checked on submit and again as the person types, so an error clears once it is fixed. */
  const blank = (value: string, message: string) => tried && value.trim() === "" ? message : null;
  const [notice, setNotice] = useState<string | null>(null);
  async function submit() {
    setBusy(true);
    setError(null);
    setNotice(null);
    const result = await addClinicPatient({
      firstName,
      lastName,
      dateOfBirth,
      email,
      practitionerUserId: picksPractitioner(clinic) ? practitionerUserId : ""
    });
    setBusy(false);
    if (result.status === "conflict") {
      setError(result.code === "already_linked" ? "That person is already linked to this clinic." : "A request to link that account is already waiting.");
      return;
    }
    if (result.status !== "ok") {
      setError("Check the name, date of birth and email, then try again.");
      return;
    }
    setTried(false);
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
    setEmail("");
    setNotice(result.added.outcome === "link_requested" ? "We emailed them to accept this clinic's request. Nothing changes until they do." : "Patient added. They have no login yet.");
    const next = await listClinicPatients();
    if (next.status === "ok") onAdded(next.clinic, result.added);
  }
  return <form noValidate className="x78zum5 xdt5ytf x626il7 xs5k50" onSubmit={event => {
    event.preventDefault();
    setTried(true);
    const filled = [firstName, lastName, dateOfBirth].every(value => value.trim() !== "");
    if (!filled || emailProblem(email) !== null) focusFirstError(event.currentTarget);else if (!busy) void submit();
  }}>
      <div className="x1983rqf x78zum5 xdt5ytf x626il7">
        <Field label="First name" id="patient-first" error={blank(firstName, "Enter the patient's first name.")}>
          {control => <Input {...control} autoComplete="off" value={firstName} onChange={setFirstName} />}
        </Field>
        <Field label="Last name" id="patient-last" error={blank(lastName, "Enter the patient's last name.")}>
          {control => <Input {...control} autoComplete="off" value={lastName} onChange={setLastName} />}
        </Field>
        <Field label="Date of birth" id="patient-dob" error={blank(dateOfBirth, "Enter the patient's date of birth.")}>
          {control => <DateInput {...control} mode="birthDate" value={dateOfBirth} onChange={setDateOfBirth} />}
        </Field>
        <Field label="Email" id="patient-email" error={tried ? emailProblem(email) : null}>
          {control => <Input {...control} type="email" autoComplete="off" value={email} onChange={setEmail} />}
        </Field>
        {picksPractitioner(clinic) && clinic.practitioners.length > 0 ? <Field label="Practitioner" id="patient-practitioner" optional>
            {control => <Select {...control} options={[{
          value: "",
          label: "None"
        }, ...clinic.practitioners.map(member => ({
          value: member.userId,
          label: member.name || member.email
        }))]} value={practitionerUserId} onChange={setPractitionerUserId} />}
          </Field> : null}
        <Button type="submit" corners="rounded" testId="add-patient" loading={busy} size="md">
          Add patient
        </Button>
      </div>
      {error === null ? null : <ErrorState title="Patient not added" body={error} />}
      {notice === null ? null : <Banner tone="ok" title="Done">
          {notice}
        </Banner>}
      <LeaveGuard dirty={[firstName, lastName, dateOfBirth, email].some(value => value.trim() !== "")} what={firstName.trim() === "" ? "the patient you started adding" : `the details you entered for ${firstName.trim().slice(0, 30)}`} />
    </form>;
}

/** An empty cell is one dash, so a column of them is quiet, and the words are there for a screen reader. */
function NothingYet({
  said
}: {
  readonly said: string;
}) {
  return <>
      <span aria-hidden="true">&mdash;</span>
      <span {...stylex.props(visuallyHidden.hidden)}>{said}</span>
    </>;
}
function PatientTable({
  patients,
  canOpen
}: {
  readonly patients: readonly ClinicPatientRow[];
  readonly canOpen: boolean;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort>({
    column: "name",
    direction: "asc"
  });
  const shown = sorted(matching(patients, query), sort);
  const columns: readonly Column<ClinicPatientRow>[] = [{
    id: "name",
    header: "Name",
    minWidth: "11rem",
    width: "30%",
    primary: true,
    sortable: true,
    truncate: true,
    cell: nameOf,
    text: nameOf
  }, {
    id: "practitioner",
    header: "Practitioner",
    minWidth: "8rem",
    sortable: true,
    truncate: true,
    cell: patient => patient.practitionerName ?? "None assigned",
    text: patient => patient.practitionerName ?? "None assigned"
  }, {
    id: "formula",
    header: "Formula",
    sortable: true,
    cell: patient => <Badge dot tone={badgeFor(patient).tone}>
          {badgeFor(patient).label}
        </Badge>
  }, {
    id: "progress",
    header: "Review and order",
    minWidth: "8rem",
    truncate: true,
    cell: patient => progressLine(patient) || <NothingYet said="None yet" />,
    text: patient => progressLine(patient) || "None yet"
  }, {
    id: "updated",
    header: "Updated",
    sortable: true,
    numeric: true,
    cell: patient => updatedOn(patient) || <NothingYet said="Not yet" />
  }];
  return <>
      {patients.length === 0 ? null : <Field label="Search patients" id="patient-search">
          {control => <Input {...control} type="search" placeholder="Name, practitioner or email" autoComplete="off" value={query} onChange={setQuery} />}
        </Field>}
      {patients.length === 0 ? null : <div role="status" data-testid="patient-count">
          <Text variant="caption" tone="secondary">
            {shown.length === patients.length ? `${String(patients.length)} ${patients.length === 1 ? "patient" : "patients"}` : `${String(shown.length)} of ${String(patients.length)} patients`}
          </Text>
        </div>}
      <Table label="Patients" columns={columns} rows={shown} rowKey={patient => patient.linkId} {...canOpen ? {
      rowHref: (patient: ClinicPatientRow) => `/clinic/patients/${patient.patientId}`,
      navigate
    } : {}} sort={sort} onSortChange={setSort} empty="No patients are linked to this clinic yet." filtered={query.trim() !== ""} noResults={<>
            <Text tone="secondary">No patient matches "{query.trim()}".</Text>
            <Button tone="quiet" size="small" corners="rounded" onClick={() => setQuery("")}>
              Show all patients
            </Button>
          </>} testId="patients" />
    </>;
}
function Home({
  clinic,
  onChange
}: {
  readonly clinic: ClinicPatientList;
  readonly onChange: (clinic: ClinicPatientList) => void;
}) {
  const canAdd = clinic.roles.includes("practitioner") || clinic.roles.includes("assistant");

  /* An owner who is not also a practitioner or assistant sees who is linked and cannot open a record (PRA-003). */
  const canOpen = clinicRolesGrant(clinic.roles, "patient.read");
  return <ClinicShell title="Patients" description={`Linked to ${clinic.organizationName}.`} signedIn clinicName={clinic.organizationName} width="wide">
      <Section title="Linked patients">
        <PatientTable patients={clinic.patients} canOpen={canOpen} />
      </Section>
      {canAdd ? <Section title="Add a patient">
          <div className="xs5k50">
            <Text tone="secondary">
              Name, date of birth and email. If they already have a Lattice account, we ask them to
              accept before this clinic can see their record.
            </Text>
          </div>
          <AddForm clinic={clinic} onAdded={next => onChange(next)} />
        </Section> : null}
    </ClinicShell>;
}
function patientRoute(path: string): {
  readonly patientId: string;
  readonly page: "detail" | "formula" | "changes";
} | null {
  const match = /^\/clinic\/patients\/([^/]+)(?:\/(formula)(?:\/(changes))?)?$/.exec(path);
  if (match?.[1] === undefined) return null;
  if (match[3] === "changes") return {
    patientId: match[1],
    page: "changes"
  };
  if (match[2] === "formula") return {
    patientId: match[1],
    page: "formula"
  };
  return {
    patientId: match[1],
    page: "detail"
  };
}

/** The paths PatientsApp answers: the list, and a patient's record, formula and changes. */
export function isPatientsPath(path: string): boolean {
  return path === "/clinic/patients" || patientRoute(path) !== null;
}
export function PatientsApp() {
  const path = usePath();
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  useEffect(() => {
    void listClinicPatients().then(loaded => {
      /* A member of several clinics who has not chosen one picks it on the clinic home. */
      if (loaded.status === "signed_out") setScreen({
        kind: "sign-in"
      });else if (loaded.status === "choose_clinic") window.location.assign("/clinic");else if (loaded.status !== "ok") setScreen({
        kind: "failed"
      });else setScreen({
        kind: "home",
        clinic: loaded.clinic
      });
    }, () => setScreen({
      kind: "failed"
    }));
  }, []);
  if (screen.kind === "sign-in") return <ClinicShell title="Sign in to the clinic" description="Use the email your clinic invited." width="narrow">
        <EmailCodeSignIn next={window.location.pathname + window.location.search} />
      </ClinicShell>;
  if (screen.kind === "loading") return <ClinicShell title="Patients" signedIn width="wide">
        <Pending message="Loading the clinic" bones={["title", "line", "line"]} />
      </ClinicShell>;
  if (screen.kind === "failed") return <ClinicShell title="Patients" signedIn width="wide">
        <ErrorState variant="page" title="We couldn't load the clinic" body="Try again in a minute. If it keeps happening, sign in again." onRetry={() => window.location.reload()} />
      </ClinicShell>;
  const route = patientRoute(path);
  if (route?.page === "formula") return <ClinicFormulaPage patientId={route.patientId} clinic={screen.clinic} />;
  if (route?.page === "changes") return <ClinicFormulaChangesPage patientId={route.patientId} clinic={screen.clinic} />;
  if (route !== null) return <ClinicPatientPage patientId={route.patientId} clinic={screen.clinic} />;
  return <Home clinic={screen.clinic} onChange={clinic => setScreen({
    kind: "home",
    clinic
  })} />;
}